import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate, Link } from "react-router-dom";
import { AlertCircle, LogOut, Menu, Search, X } from "lucide-react";

import { useAuth } from "../auth/AuthProvider";
import { initials } from "../lib/format";
import { PERSONA_DESK_LABEL, usePersona } from "../lib/persona";
import {
  ORGANISATION,
  PLATFORM,
  REPORTS,
  SELF_SERVICE,
  SETTINGS,
  canSee,
  pillarsFor,
} from "./navigation";
import { cx } from "../components/ui";
import type { CurrentUser } from "../api/types";

function isPlatformOperator(user: CurrentUser | null): boolean {
  return Boolean(user?.is_platform_operator);
}

export function AppShell() {
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const persona = usePersona();

  const pillars = pillarsFor(persona);
  const organisation = ORGANISATION.filter((item) => canSee(item, persona));
  const settings = SETTINGS.filter((item) => canSee(item, persona));
  const reportsVisible = canSee(REPORTS, persona);

  // Route changes close the mobile drawer. Without this the drawer stays open
  // over the page you just navigated to.
  useEffect(() => setNavOpen(false), [location.pathname]);

  const profileComplete = (user as any)?.profile_complete !== false

  return (
    <div className="min-h-dvh bg-slate-50">
      {/* Scrim: mobile only, and only while the drawer is open. */}
      {navOpen ? (
        <button
          aria-label="Close navigation"
          onClick={() => setNavOpen(false)}
          className="fixed inset-0 z-30 bg-slate-900/40 lg:hidden"
        />
      ) : null}

      <aside
        className={cx(
          "fixed inset-y-0 left-0 z-40 flex w-72 flex-col border-r border-slate-200 bg-white print:hidden",
          "transition-transform duration-200 lg:translate-x-0",
          navOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <SidebarHeader onClose={() => setNavOpen(false)} />
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {organisation.length ? (
            <NavGroup label="Organisation">
              {organisation.map((item) => (
                <NavRow
                  key={item.to}
                  to={item.to}
                  icon={item.icon}
                  label={item.label}
                  planned={!item.built}
                  end={item.end}
                />
              ))}
            </NavGroup>
          ) : null}

          <NavGroup label="My work">
            {SELF_SERVICE.filter((item) => canSee(item, persona)).map((item) => (
              <NavRow
                key={item.to}
                to={item.to}
                icon={item.icon}
                label={item.to === "/people/desk" ? PERSONA_DESK_LABEL[persona] : item.label}
              />
            ))}
          </NavGroup>

          {pillars.map((pillar) => (
            <NavGroup
              key={pillar.id}
              label={persona === "employee" && pillar.employeeLabel ? pillar.employeeLabel : pillar.label}
            >
              {pillar.items.map((item) => (
                <NavRow
                  key={item.to}
                  to={item.to}
                  icon={item.icon}
                  label={item.label}
                  planned={!item.built}
                  end={item.end}
                />
              ))}
            </NavGroup>
          ))}

          {reportsVisible ? (
            <NavGroup label={REPORTS.label}>
              {REPORTS.items
                .filter((item) => canSee(item, persona))
                .map((item) => (
                  <NavRow
                    key={item.to}
                    to={item.to}
                    icon={item.icon}
                    label={item.label}
                    planned={!item.built}
                  />
                ))}
            </NavGroup>
          ) : null}

          {settings.length ? (
            <NavGroup label="Settings">
              {settings.map((item) => (
                <NavRow
                  key={item.to}
                  to={item.to}
                  icon={item.icon}
                  label={item.label}
                  end={item.to === "/settings"}
                />
              ))}
            </NavGroup>
          ) : null}

          {isPlatformOperator(user) ? (
            <NavGroup label="Platform">
              {PLATFORM.map((item) => (
                <NavRow key={item.to} to={item.to} icon={item.icon} label={item.label} />
              ))}
            </NavGroup>
          ) : null}
        </nav>
      </aside>

      <div className="lg:pl-72 print:pl-0">
        <TopBar onOpenNav={() => setNavOpen(true)} />
        {user && user.role !== "owner" && profileComplete === false ? (
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-6xl flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="text-amber-600" size={18} />
                <p className="text-sm text-amber-800">
                  <strong>Complete your profile</strong> - Some personal details are incomplete.
                </p>
              </div>
              <Link to="/people/me" className="text-sm font-medium text-amber-900 underline hover:no-underline">
                Complete now
              </Link>
            </div>
          </div>
        ) : null}
        <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8 print:max-w-none print:px-0 print:py-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function SidebarHeader({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const account = user?.account;

  return (
    <div className="flex h-16 items-center gap-3 border-b border-slate-200 px-4">
      <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-slate-900 text-sm font-semibold text-white">
        S
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-slate-900">
          {account?.name ?? "SBM One"}
        </p>
        <p className="truncate text-xs capitalize text-slate-500">
          {account?.industry?.replace(/_/g, " ") ?? "Not set"}
          {account?.plan ? ` · ${account.plan}` : ""}
        </p>
      </div>
      <button
        onClick={onClose}
        aria-label="Close navigation"
        className="rounded p-1 text-slate-400 hover:bg-slate-100 lg:hidden"
      >
        <X size={18} />
      </button>
    </div>
  );
}

function NavGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-5">
      <p className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
        {label}
      </p>
      <ul className="space-y-0.5">{children}</ul>
    </div>
  );
}

function NavRow({
  to,
  icon: Icon,
  label,
  planned,
  end,
}: {
  to: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  planned?: boolean;
  end?: boolean;
}) {
  return (
    <li>
      <NavLink
        to={to}
        end={end}
        className={({ isActive }) =>
          cx(
            "flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition",
            isActive
              ? "bg-slate-900 font-medium text-white"
              : "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
          )
        }
      >
        <Icon size={17} />
        <span className="flex-1 truncate">{label}</span>
        {planned ? (
          // A quiet marker, not a disabled row: the route exists and explains
          // itself, which is more use than a dead link.
          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-400">
            Soon
          </span>
        ) : null}
      </NavLink>
    </li>
  );
}

function TopBar({ onOpenNav }: { onOpenNav: () => void }) {
  const { user, signOut } = useAuth();
  return (
    <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur sm:px-6 lg:px-8 print:hidden">
      <button
        onClick={onOpenNav}
        aria-label="Open navigation"
        className="rounded p-1.5 text-slate-500 hover:bg-slate-100 lg:hidden"
      >
        <Menu size={20} />
      </button>

      <GlobalSearch />

      <div className="ml-auto flex items-center gap-3">
        <span className="hidden text-xs text-slate-400 sm:inline">
          Press <kbd className="rounded border border-slate-200 px-1">/</kbd> to search
        </span>
        {user ? (
          <div className="flex items-center gap-1 rounded-md px-1 py-1">
            <Link
              to="/people/me"
              className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-slate-100"
              aria-label="Open your profile"
            >
              <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-600">
                {initials(user?.full_name || user?.email || "?")}
              </div>
              <div className="hidden text-left sm:block">
                <p className="text-sm font-medium text-slate-900 leading-tight truncate max-w-[160px]">
                  {user?.full_name || user?.email}
                </p>
                <p className="text-xs capitalize text-slate-500 leading-tight">{user?.role}</p>
              </div>
            </Link>
            <button
              onClick={signOut}
              title="Sign out"
              aria-label="Sign out"
              className="self-end rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            >
              <LogOut size={14} />
            </button>
          </div>
        ) : null}
      </div>
    </header>
  );
}

/**
 * Global search is scoped to contacts, the only index that exists. It is honest
 * about that: the placeholder names what it searches, so nobody waits on a
 * cross-entity search that is not wired up.
 *
 * The accessible name is deliberately distinct from the contacts page's own
 * filter input. Two controls sharing a name is an accessibility defect -- a
 * screen reader announces them identically -- and it makes the page ambiguous
 * for anything driving the DOM.
 */
function GlobalSearch() {
  const [value, setValue] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      const typingElsewhere =
        target?.tagName === "INPUT" || target?.tagName === "TEXTAREA";
      if (event.key === "/" && !typingElsewhere) {
        event.preventDefault();
        document.getElementById("global-search")?.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    const query = value.trim();
    // `navigate`, not `location.assign`: a full reload throws away the session
    // we just resumed and re-runs every query.
    navigate(query ? `/customers/contacts?search=${encodeURIComponent(query)}` : "/customers/contacts");
  }

  return (
    <form onSubmit={onSubmit} className="relative w-full max-w-md">
      <Search
        size={16}
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
      />
      <input
        id="global-search"
        type="search"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder="Search contacts"
        aria-label="Search all contacts"
        className="w-full rounded-md border-0 bg-slate-100 py-2 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:ring-2 focus:ring-inset focus:ring-slate-900"
      />
    </form>
  );
}